/** SUNTRONIX ONE — Administration → ONE CONNECT 3.0 control centre. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Radar, Activity, Webhook, RefreshCw, PlugZap, ShieldAlert, CheckCircle2 } from "lucide-react";

const ST_TINT = {
  CONNECTED: "bg-emerald-100 text-emerald-800",
  SETUP_REQUIRED: "bg-amber-100 text-amber-800",
  DEGRADED: "bg-orange-100 text-orange-800",
  AUTH_EXPIRED: "bg-rose-100 text-rose-800",
  RATE_LIMITED: "bg-fuchsia-100 text-fuchsia-800",
  DISABLED: "bg-slate-200 text-slate-600",
  ERROR: "bg-rose-100 text-rose-800",
  TEMPORARILY_DISABLED: "bg-slate-200 text-slate-500",
};
const WH_TINT = { PROCESSED: "bg-emerald-100 text-emerald-700", RECEIVED: "bg-sky-100 text-sky-700", RETRY_SCHEDULED: "bg-amber-100 text-amber-800", DLQ: "bg-rose-100 text-rose-700", REPLAYING: "bg-indigo-100 text-indigo-700" };
const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");

export default function OneConnect3() {
  const [matrix, setMatrix] = useState(null);
  const [health, setHealth] = useState(null);
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("matrix");

  const load = useCallback(() => {
    api.get("/one-connect/v3/matrix").then(({ data }) => setMatrix(data)).catch(() => {});
    api.get("/one-connect/v3/health").then(({ data }) => setHealth(data)).catch(() => {});
    api.get("/one-connect/v3/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/one-connect/v3/webhooks/events?limit=60").then(({ data }) => setEvents(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const test = async (key, name) => {
    try {
      const { data } = await api.post(`/one-connect/v3/connectors/${key}/test`);
      const r = data.result;
      const pass = r.authentication === "PASS";
      (pass ? toast.success : toast.error)(`${name}: AUTH ${r.authentication} · PERM ${r.permission} · API ${r.api} · WH ${r.webhook}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };
  const replay = async (id) => {
    try { const { data } = await api.post(`/one-connect/v3/webhooks/${id}/replay`); toast.success(`Replayed → ${data.status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Replay failed"); }
  };

  if (!matrix || !health || !status) return <div className="p-8 text-center text-slate-400" data-testid="oc3-loading">Loading…</div>;

  const ready = status.final_status === "ONE CONNECT 3.0 READY";
  const cards = health.cards || {};

  return (
    <div className="mx-auto max-w-[1200px] space-y-4" data-testid="one-connect-3-page">
      <header className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-indigo-900 text-white"><Radar className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">ONE CONNECT 3.0</div>
          <div className="text-[11px] text-slate-500">Enterprise API &amp; Integration Control Centre — external data is evidence, never automatic truth</div>
        </div>
        <span data-testid="oc3-final-status" className={`rounded-md px-3 py-1.5 text-[11px] font-semibold ${ready ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>
          {ready ? <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> : <ShieldAlert className="mr-1 inline h-3.5 w-3.5" />}{status.final_status}
        </span>
      </header>

      {/* Health cards */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" data-testid="oc3-health-cards">
        {[["CONNECTED", "Connected"], ["SETUP_REQUIRED", "Setup req."], ["DEGRADED", "Degraded"], ["FAILED", "Failed"], ["WEBHOOK_ERROR", "Webhook err"], ["AUTH_EXPIRING", "Auth exp."], ["TEMPORARILY_DISABLED", "Disabled"]].map(([k, lbl]) => (
          <div key={k} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`oc3-card-${k}`}>
            <div className="text-2xl font-heading font-semibold text-slate-900">{cards[k] ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">{lbl}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        {[["matrix", "Connector Matrix", Activity], ["webhooks", "Webhook Gateway", Webhook]].map(([k, lbl, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`oc3-tab-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold ${tab === k ? "bg-indigo-900 text-white" : "border border-slate-200 text-slate-600"}`}>
            <Icon className="h-3.5 w-3.5" /> {lbl}
          </button>
        ))}
        <button onClick={load} className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1.5 text-[11px] text-slate-600"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </div>

      {tab === "matrix" && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="oc3-matrix">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Connector</th><th className="px-3 py-2">Env</th><th className="px-3 py-2">Auth</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2">Last sync</th><th className="px-3 py-2">Webhook</th>
                <th className="px-3 py-2">Tenant</th><th className="px-3 py-2">Records</th><th className="px-3 py-2">Errors</th>
                <th className="px-3 py-2">Action required</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((r) => (
                <tr key={r.key} className="border-t border-slate-100" data-testid={`oc3-row-${r.key}`}>
                  <td className="px-3 py-1.5 font-medium text-slate-800">{r.connector}<div className="text-[9px] text-slate-400">{r.provider} · {r.priority}</div></td>
                  <td className="px-3 py-1.5 text-slate-500">{r.environment}</td>
                  <td className="px-3 py-1.5 font-mono text-[9px] text-slate-500">{r.auth}</td>
                  <td className="px-3 py-1.5"><span data-testid={`oc3-status-${r.key}`} className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${ST_TINT[r.status] || "bg-slate-100"}`}>{r.status.replace(/_/g, " ")}</span></td>
                  <td className="px-3 py-1.5 text-[9px] text-slate-500">{fmtDate(r.last_sync)}</td>
                  <td className="px-3 py-1.5 text-[9px] text-slate-500">{r.webhook ? (r.webhook_last_received ? fmtDate(r.webhook_last_received) : "—") : "N/A"}</td>
                  <td className="px-3 py-1.5"><span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-600">{r.tenant}</span></td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{r.records ?? 0}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${r.errors ? "text-rose-600 font-semibold" : "text-slate-400"}`}>{r.errors ?? 0}</td>
                  <td className="px-3 py-1.5 text-[10px] text-slate-500">{r.action_required || "—"}</td>
                  <td className="px-3 py-1.5">
                    <button onClick={() => test(r.key, r.connector)} data-testid={`oc3-test-${r.key}`} className="inline-flex items-center gap-1 rounded border border-indigo-300 px-2 py-0.5 text-[9px] font-semibold text-indigo-700"><PlugZap className="h-3 w-3" /> Test</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "webhooks" && (
        <div className="rounded-xl border border-slate-200 bg-white" data-testid="oc3-webhooks">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-[12px] font-semibold"><Webhook className="h-3.5 w-3.5" /> ONE Webhook Gateway — signed · idempotent · retry + DLQ + replay</div>
          <div className="overflow-x-auto"><table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500"><tr>
              <th className="px-3 py-2">Provider</th><th className="px-3 py-2">Event ID</th><th className="px-3 py-2">Signature</th>
              <th className="px-3 py-2">Received</th><th className="px-3 py-2">Attempts</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-t border-slate-100" data-testid={`oc3-wh-${e.id}`}>
                  <td className="px-3 py-1.5 font-medium text-slate-700">{e.provider}</td>
                  <td className="px-3 py-1.5 font-mono text-[9px] text-slate-500">{e.event_id}</td>
                  <td className="px-3 py-1.5 text-[9px] text-slate-500">{e.signature_state}</td>
                  <td className="px-3 py-1.5 text-[9px] text-slate-500">{fmtDate(e.received_at)}</td>
                  <td className="px-3 py-1.5 text-center tabular-nums">{e.attempts}</td>
                  <td className="px-3 py-1.5"><span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${WH_TINT[e.status] || "bg-slate-100 text-slate-600"}`}>{e.status}</span></td>
                  <td className="px-3 py-1.5">{(e.status === "DLQ" || e.status === "RETRY_SCHEDULED") && <button onClick={() => replay(e.id)} data-testid={`oc3-replay-${e.id}`} className="rounded border border-indigo-300 px-2 py-0.5 text-[9px] font-semibold text-indigo-700">Replay</button>}</td>
                </tr>
              ))}
              {!events.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No webhook activity yet.</td></tr>}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}
